import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import { Button, toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';
import { useRole, useStore } from '@feathr/report_components/state';

import type { IDonationEngagementParams } from './DonationFormEngagementCard';
import {
  donationEngagementFlavorLabel,
  type EDonationEngagementFlavor,
} from './DonationFormInteractionSelect';

interface IProps {
  form: DonationForm;
  flavor: EDonationEngagementFlavor;
  params: IDonationEngagementParams;
  localUrl: (url: string) => string;
}

function DonationFormCreateGroupButton({
  form,
  flavor,
  params,
  localUrl,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const { hasSegments } = useRole();

  async function handleCreateGroup(): Promise<void> {
    try {
      // Drop query-time presentation metadata and mint row keys before persisting
      // these query filters as an editable segment.
      const predicates = params.predicates.map(({ inner_hits: _ignored, ...rest }) =>
        ensurePredicateKeys(rest),
      );
      const name = `${form.name} -- ${donationEngagementFlavorLabel(t)[flavor]}`;
      const segment = Segments.create({
        name,
        predicates,
        mode: params.mode,
        lookback_mode: params.lookback_mode,
      });
      const savedSegment = await Segments.add(segment);
      if (savedSegment.isErrored) {
        throw savedSegment.error;
      }
      toast(t('Your group was successfully created.'), { type: 'success' });
      window.open(localUrl(savedSegment.getItemUrl()), '_blank');
    } catch (error) {
      toast(
        t('There was an error creating your group: {{error}}', {
          error: errorMessage(error, t),
        }),
        { type: 'error' },
      );
    }
  }

  return (
    <Button
      disabled={!hasSegments}
      name={'create-group-button'}
      onClick={handleCreateGroup}
      target={'_blank'}
      tooltip={!hasSegments ? t("You don't have access to create a group.") : undefined}
    >
      {t('Create group')}
    </Button>
  );
}

export default observer(DonationFormCreateGroupButton);
